import {
  Button,
  Card,
  Divider,
  Flex,
  Input,
  Popconfirm,
  Table,
  Typography,
  message,
  AutoComplete,
  Modal,
  Select,
} from "antd";
import { useState, useEffect } from "react";
import ErrorPage from "components/ui/ErrorPage";
import { useGetCandidatesList } from "api/users/Candidates/useGetCandidates";
import { CandidateInterface } from "api/users/Candidates/dto";
import { /*QueryParamsStatus, */useFilterPagination } from "hooks/useFilterPagination";
import { APPLICATION_STATUS } from "enum/job-status-type";
import { useGetJobsByRecruiter } from "api/jobs/useGetJobsByRecruiter";
import { GetRecruiterJobsInterface } from "api/jobs/dto";
import { useUpdateCandidateApplication } from "api/users/Candidates/useUpdateCandidateStatus";
import { storage } from "utils/storage";
import sendMessage from "api/messages/useSendMessage";
import CandidateProfile from "views/Profile/CandidateProfile";
// import { getDocs, query, collection, where } from "firebase/firestore";
// import { db } from "../../firebase/firebase";
// import { getDocs, query, collection, where } from "firebase/firestore";
// import { db } from "../../firebase/firebase";

const { Title } = Typography;

type ApplicationPayload = {
  id: string;
  applicantId: string;
  status: string;
  jobId: string;
};



const CandidatesList = () => {

  const statusOptions = [
    { label: "All", value: "", disabled: false },
    { label: "Approved", value: APPLICATION_STATUS.APPROVED },
    { label: "Rejected", value: APPLICATION_STATUS.REJECTED },
    { label: "Shortlisted", value: APPLICATION_STATUS.SHORT_LISTED },
    { label: "Submitted", value: APPLICATION_STATUS.SUBMITTED },
  ];

  const user = storage.getUserFromStorage();
  const { queryParamaters, /*onSearch,*/ onTableChange, /*onFilterChange*/ } = useFilterPagination();
  const [appliationPayload, setApplicationPayload] = useState<ApplicationPayload | null>(null);

  const [recruiterJobList, setRecruiterJobList] = useState<GetRecruiterJobsInterface | null>(null);
  const [selectedJob, setSelectedJob] = useState<string | undefined>(undefined);
  const [searchValue, setSearchValue] = useState<string>("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFilterStatus, setSelectedFilterStatus] = useState<string>("")


  const { data: jobData } = useGetJobsByRecruiter({ ...queryParamaters, recruiterId: user.uid });

  const [selectedStatus, setSelectedStatus] = useState<{ [key: string]: string }>({}); // Change state to an object with `cid`-`status` pairs

  const handleStatusFilterChange = (value: string) => {
    setSelectedFilterStatus(value);
  };

  useEffect(() => {
    if (jobData?.data) {
      setRecruiterJobList(jobData);
    }
  }, [jobData]);

  // Fetch candidates based on selected job only if a job is selected
  const { data, isFetching, error } = useGetCandidatesList({
    ...queryParamaters,
    jobId: selectedJob || "",
  });

  const filteredCandidates = data?.data?.filter(
    (candidate) => !selectedFilterStatus || candidate.status === selectedFilterStatus
  );

  useEffect(() => {
    // Update selectedStatus for each candidate
    if (data?.data) {
      const updatedStatus: { [key: string]: string } = {};
      data.data.forEach((candidate) => {
        updatedStatus[candidate.id] = candidate.status; // Update status for each cid
      });
      setSelectedStatus(updatedStatus);
    }
  }, [data]); // Runs when data changes

  const getStatusColor = (status: string) => {
    switch (status) {
      case APPLICATION_STATUS.REJECTED:
        return "red";
      case APPLICATION_STATUS.APPROVED:
        return "green";
      case APPLICATION_STATUS.SHORT_LISTED:
        return "blue";
      default:
        return "black";
    }
  };

  const handleJobSearch = (value: string) => {
    setSearchValue(value);
    if (!value) {
      setSelectedJob(undefined);
    }
  };

  const handleSearchSelect = (value: string) => {
    setSearchValue(value);
    const selectedJob = recruiterJobList?.data?.find((job) => job.jobTitle === value);
    if (selectedJob) {
      setSelectedJob(selectedJob.id);
    }
  };

  const updateJobApplicationMutation = useUpdateCandidateApplication();

  const confirm = async () => {
    try {
      if (!appliationPayload) {
        message.error("Candidate not selected");
        return;
      }

      const { applicantId, jobId, id, status } = appliationPayload; // Extract status from payload
      const selectedJobDetails = recruiterJobList?.data?.find((job) => job.id === jobId);
      if (!selectedJobDetails) {
        message.error("Job not found");
        return;
      }
      const jobTitle = selectedJobDetails.jobTitle || "";
      const companyName = selectedJobDetails.companyName || "";
      
      // Only send congratulation message if status is APPROVED
      if (status === APPLICATION_STATUS.APPROVED) {
        const hiringMessage = `Congratulations! You have been hired for ${jobTitle} at ${companyName}`;
        await sendMessage({
          sender: user.uid,
          recipient: applicantId,
          jobId,
          companyName,
          jobTitle,
          status: "unread",
          text: hiringMessage,
        });
      }

      const payload = {
        id,
        jobId,
        applicantId,
        status, // Use the status from the payload instead of hardcoding
      };

      updateJobApplicationMutation.mutate(payload, {
        onSuccess: (data: any) => {
          setSelectedStatus((prevStatus) => ({
            ...prevStatus,
            [payload.id]: payload.status,
          }));
          
          // Show appropriate success message based on status
          if (status === APPLICATION_STATUS.APPROVED) {
            message.success("Candidate hired successfully");
          } else if (status === APPLICATION_STATUS.REJECTED) {
            message.success("Candidate rejected successfully");
          } else if (status === APPLICATION_STATUS.SHORT_LISTED) {
            message.success("Candidate shortlisted successfully");
          } else {
            message.success("Candidate status updated successfully");
          }
        },
      });
    } catch (error: any) {
      message.error(error.message);
    }
  };


  const columns = [
    {
      title: "Candidate",
      dataIndex: "candidate",
      key: "candidate",
      render: (__: any, record: CandidateInterface) => (
        <div style={{ textTransform: "capitalize" }}>
          {record?.enumerator?.firstName} {record?.enumerator?.lastName}
        </div>
      ),
    },
    {
      title: "Job Title",
      dataIndex: "jobTitle",
      key: "jobTitle",
      render: (__: any, record: CandidateInterface) => <div>{record?.job?.jobTitle}</div>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (__: any, record: CandidateInterface) => <div>{record?.enumerator?.email}</div>,
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (__: any, record: CandidateInterface) => <div>{record?.enumerator?.phoneNumber}</div>,
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      render: (__: any, record: CandidateInterface) => <div>{record?.job?.companyName}</div>,
    },

    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (__: any, record: CandidateInterface) => {
        const status = selectedStatus[record.id] || "";

        return (
          <>
            <Flex gap={"small"}>
              {status !== APPLICATION_STATUS.SHORT_LISTED && status !== APPLICATION_STATUS.APPROVED && status !== APPLICATION_STATUS.REJECTED && (
                <Popconfirm
                  title="Are you sure you want to shortlist this candidate?"
                  description=""
                  onConfirm={confirm}
                  okButtonProps={{ type: "primary", style: { backgroundColor: "#22AD5C" } }}
                >
                  <Button
                    style={{ backgroundColor: "#FBBF24" }}
                    type="primary"
                    className="h-9 px-4 border-none"
                    onClick={() =>
                      setApplicationPayload({
                        applicantId: record?.enumerator?.uid,
                        status: APPLICATION_STATUS.SHORT_LISTED,
                        jobId: record?.job?.id,
                        id: record?.id,
                      })
                    }
                  >
                    Shortlist
                  </Button>
                </Popconfirm>
              )}

              {status === APPLICATION_STATUS.SHORT_LISTED &&  (
                  <Popconfirm
                    title="Are you sure you want to hire this candidate?"
                    description=""
                    onConfirm={confirm}
                    okButtonProps={{ type: "primary", style: { backgroundColor: "#22AD5C" } }}
                  >
                    <Button
                      type="primary"
                      className="h-9 px-4 bg-primary text-white border-none"
                      onClick={() =>
                        setApplicationPayload({
                          applicantId: record?.enumerator?.uid,
                          status: APPLICATION_STATUS.APPROVED,
                          jobId: record?.job?.id,
                          id: record?.id,
                        })
                      }
                    >
                      Hire
                    </Button>
                  </Popconfirm> )}

              {status !== APPLICATION_STATUS.APPROVED && status !== APPLICATION_STATUS.REJECTED && (
                <>
                  

                  <Popconfirm
                    title="Are you sure you want to reject this candidate?"
                    description=""
                    onConfirm={confirm}
                    okButtonProps={{ type: "primary", style: { backgroundColor: "#22AD5C" } }}
                  >
                    <Button
                      onClick={() =>
                        setApplicationPayload({
                          applicantId: record?.enumerator?.uid,
                          status: APPLICATION_STATUS.REJECTED,
                          jobId: record?.job?.id,
                          id: record?.id,
                        })
                      }
                    >
                      Reject
                    </Button>
                  </Popconfirm>
                </>
              )}

              <Button
                style={{ backgroundColor: "#1677ff", color: "white", border: "none" }}
                onClick={() => { setIsModalOpen(true) }}            >
                View Details
              </Button>
              <Modal
                title="Candidate Profile"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                className=" mx-auto px-5 "
                width={1100}
              >
                <CandidateProfile id={record?.enumerator?.uid} />
              </Modal>
            </Flex>
          </>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (__: any, record: CandidateInterface) => {
        // Use `selectedStatus` to get the status by `cid`
        const status = selectedStatus[record.id] || ""; // Get status for the current `cid` (record.id)
        return <div style={{ color: getStatusColor(status) }}>{status}</div>;
      },
    },

  ]

  if (error) {
    return <ErrorPage error={error} />;
  }

  // Filter job suggestions based on search query
  const filteredJobs = recruiterJobList?.data?.filter((job) =>
    job.jobTitle.toLowerCase().includes(searchValue.toLowerCase())
  );

  return (
    <>
      <div className="bg-background">
        <div className="max-w-8xl mx-auto py-12">
          <Card>
            <Title level={2} style={{ fontWeight: 700 }}>
              Candidates List
            </Title>
            <div style={{ display: "flex", justifyContent: "center", marginTop: "22px" }}>
              <AutoComplete
                value={searchValue}
                onChange={handleJobSearch}
                onSelect={handleSearchSelect}
                options={filteredJobs?.map((job) => ({
                  label: (
                    <div key={job.id} style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{job.jobTitle}</span>
                      <span style={{ color: "#888" }}>{new Date(job.createdAt).getTime()}</span>
                    </div>
                  ),
                  value: job.jobTitle,
                }))}
                style={{ width: 350 }}
              >
                <Input.Search
                  placeholder="Search for job"
                  enterButton="Search"
                  size="middle"
                  className="__search__input"
                />
              </AutoComplete>
            </div>

            {!selectedJob && (
              <div style={{ textAlign: "center", padding: "50px 0" }}>
                <Title level={4} style={{ color: "#888", fontWeight: 500 }}>
                  Please select one of your jobs to display the candidates
                </Title>
              </div>
            )}

            {selectedJob && (
              <>
                <div className="flex-row justify-start mt-10 _">
                  <Select
                    allowClear
                    value={selectedFilterStatus}
                    placeholder="Sort by Status"
                    className="__job__type border-none w-56"
                    options={statusOptions}
                    onChange={handleStatusFilterChange}
                  />
                </div>
                <Divider />
                <Table
                  dataSource={filteredCandidates}
                  columns={columns}
                  className="mt-12"
                  loading={isFetching}
                  pagination={{
                    total: filteredCandidates?.length,
                    current: data?.page,
                    defaultPageSize: 10,
                    defaultCurrent: 1,
                    onChange: onTableChange,
                    position: ["bottomLeft"],
                  }}
                  scroll={{ x: true }}
                />
              </>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};

export default CandidatesList;